import { AlertTriangle, TrendingUp, Eye, CheckCircle } from 'lucide-react';
import {
  getStatus,
  getStatusLabel,
  getStatusColor,
  getOpportunityStatus,
  getOpportunityStatusLabel,
  getOpportunityStatusColor,
  getTrajectoryArrow,
  getTrajectoryColor,
  type Trajectory,
} from '@/data/regulatoryData';
import { CountryScore } from '@/services/scoreService';
import { FlagIcon } from '@/components/FlagIcon';

interface CountryTooltipProps {
  country: CountryScore;
  position: { x: number; y: number };
  lastUpdated?: string;
}

const StatusIcon = ({ status }: { status: string }) => {
  const iconClass = "h-3 w-3";
  switch (status) {
    case 'high':
      return <AlertTriangle className={iconClass} />;
    case 'tightening':
      return <TrendingUp className={iconClass} />;
    case 'watch':
      return <Eye className={iconClass} />;
    default:
      return <CheckCircle className={iconClass} />;
  }
};

const CountryTooltip = ({ country, position }: CountryTooltipProps) => {
  const status = getStatus(country.score);
  const statusLabel = getStatusLabel(status);
  const statusColor = getStatusColor(status);

  // Opportunity score
  const opportunityScore = country.opportunity_score ?? (100 - country.score);
  const oppStatus = getOpportunityStatus(opportunityScore);
  const oppStatusLabel = getOpportunityStatusLabel(oppStatus);
  const oppStatusColor = getOpportunityStatusColor(oppStatus);

  // Trajectory
  const trajectory = (country.trajectory || 'stable') as Trajectory;
  const trajectoryArrow = getTrajectoryArrow(trajectory);
  const trajectoryColor = getTrajectoryColor(trajectory);

  const deltaPrefix = country.delta_30d > 0 ? '↑' : country.delta_30d < 0 ? '↓' : '';
  const deltaValue = country.delta_30d > 0 ? `+${country.delta_30d}` : country.delta_30d.toString();
  const deltaColorClass = country.delta_30d > 0 ? 'text-delta-negative' : country.delta_30d < 0 ? 'text-delta-positive' : 'text-text-tertiary';

  // Position tooltip with offset, keeping it within viewport
  const tooltipStyle: React.CSSProperties = {
    left: Math.min(position.x + 16, window.innerWidth - 340),
    top: Math.min(position.y - 20, window.innerHeight - 350),
  };

  return (
    <div
      className="fixed z-[10000] pointer-events-none animate-tooltip-enter"
      style={tooltipStyle}
    >
      <div className="glass-strong rounded-lg shadow-tooltip p-4 w-[300px]">
        {/* Country Name + Trajectory */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FlagIcon code={country.code} size="xs" />
            <h3 className="text-text-primary font-semibold text-base">
              {country.name}
            </h3>
          </div>
          <span
            className="text-sm px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${trajectoryColor}20`, color: trajectoryColor }}
          >
            {trajectoryArrow}
          </span>
        </div>

        {/* Data confidence warning */}
        {(country.stale || country.low_confidence) && (
          <div className="flex items-center gap-1.5 text-xs text-amber-400 bg-amber-400/10 rounded px-2 py-1 mb-3">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            <span>{country.stale ? 'Stale data' : 'Limited signals'}</span>
          </div>
        )}

        {/* Dual Score Display */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Pressure */}
          <div className="space-y-1">
            <div className="text-text-secondary text-xs">Pressure</div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-text-primary">{country.score}</span>
              <span
                className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: `${statusColor}20`,
                  color: statusColor
                }}
              >
                <StatusIcon status={status} />
                {statusLabel}
              </span>
            </div>
          </div>

          {/* Opportunity */}
          <div className="space-y-1 border-l border-border pl-3">
            <div className="text-text-secondary text-xs">Opportunity</div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-text-primary">{opportunityScore.toFixed(0)}</span>
              <span
                className="text-xs font-medium px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: `${oppStatusColor}20`,
                  color: oppStatusColor
                }}
              >
                {oppStatusLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Delta */}
        <div className={`text-sm mb-4 ${deltaColorClass}`}>
          {deltaPrefix} {deltaValue} vs 30 days ago
        </div>

        {/* Key Drivers (condensed) */}
        <div className="mb-4">
          <p className="text-text-secondary text-xs uppercase tracking-wide mb-2">
            Key drivers:
          </p>
          <ul className="space-y-1">
            {country.drivers.slice(0, 2).map((driver, index) => (
              <li key={index} className="text-text-primary text-sm flex items-start gap-2">
                <span className="text-text-tertiary mt-0.5">•</span>
                <span className="line-clamp-1">{driver}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Latest Signal */}
        {country.latest_signal && (
          <div className="mb-4">
            <p className="text-text-secondary text-xs uppercase tracking-wide mb-1">
              Latest signal:
            </p>
            <p className="text-text-primary text-sm line-clamp-2">
              {country.latest_signal.text}
            </p>
            <p className="text-text-tertiary text-xs mt-0.5">
              {country.latest_signal.days_ago} {country.latest_signal.days_ago === 1 ? 'day' : 'days'} ago
            </p>
          </div>
        )}

        {/* Click for details hint */}
        <div className="pt-3 border-t border-border text-center">
          <span className="text-text-tertiary text-xs">Click for details →</span>
        </div>
      </div>
    </div>
  );
};

export default CountryTooltip;
